import type { SchemaProperty } from 'types/schema';

/**
 * Fans a whole-aspect renderer's emitted value bag out to per-property writes (EAINT-855 SC1).
 * Extracted from `SphereProperties`'s `WholeAspectRenderer` so the ADR-022 write gate it enforces
 * is unit-testable in isolation — it is the ONLY path a value takes from an author-supplied
 * whole-aspect component into Formik, and therefore the point where the gate is actually enforced.
 *
 * `groupPropertiesByAspect` already degrades a group containing any write-blocked property to
 * `view` (so no `onChange` is wired at all in that case). This filter is the non-bypassable
 * backstop: the gate must not depend on the author's component honoring the advisory
 * `writeBlockedProperties` prop the host passes alongside it.
 *
 * Only changed keys are written, and only for properties the aspect actually contributes — a
 * component emitting extra keys cannot write properties outside its own aspect.
 */
export function fanOutAspectBagChange(
  newBag: unknown,
  properties: SchemaProperty[],
  previousBag: Record<string, any>,
  onChange: (propertyName: string, value: any) => void,
  writeBlockedProps?: Set<string>
): void {
  if (!newBag || typeof newBag !== 'object') return;
  const bag = newBag as Record<string, any>;
  for (const prop of properties) {
    if (writeBlockedProps?.has(prop.name)) continue;
    const next = bag[prop.name];
    if (next !== previousBag[prop.name]) onChange(prop.name, next);
  }
}
